import {
  getOrderHistoryListQuery,
  getUserOrdersQuery,
} from '@/api/orders/orderQueries';
import type {
  OrderHistoryFilters as OrderHistoryFiltersType,
  OrderHistoryResponse,
} from '@/api/orders/orderTypes';
import { TableCell, TableHead, TableRow } from '@/components/common/Table';
import { Role } from '@/domain/Role';
import { useHasRole } from '@/hooks/useHasRole';
import { formatDate } from '@/lib/utils/dateUtils';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useCallback, useState } from 'react';
import { DashboardTableAccordion } from '../../DashboardTableAccordion';
import UserCard from '../orders-common/UserCard';
import { OrderHistoryFilters } from './OrderHistoryFilters';

interface UserOrdersRowsProps {
  userId: number;
  expandedOrderId: number | null;
  onExpandToggle: (orderId: number) => void;
}

function UserOrdersRows({
  userId,
  expandedOrderId,
  onExpandToggle,
}: UserOrdersRowsProps) {
  const { data: response, isLoading } = useQuery(getUserOrdersQuery(userId));
  const userOrders = response?.data || [];

  if (isLoading) {
    return (
      <TableRow className="border-none hover:bg-transparent">
        <TableCell colSpan={5} className="py-2 text-center text-slate-500">
          Loading orders...
        </TableCell>
      </TableRow>
    );
  }

  return (
    <Fragment>
      {userOrders.map((order) => (
        <Fragment key={order.id}>
          <TableRow className="border-none hover:bg-transparent">
            <TableCell colSpan={2} className="py-4 pl-4">
              <span>Order ID: {order.id}</span>
            </TableCell>
            <TableCell colSpan={2} className="py-4"></TableCell>
            <TableCell className="py-4">
              <button
                onClick={() => onExpandToggle(order.id)}
                className="text-primary font-medium hover:underline"
              >
                {expandedOrderId === order.id ? 'Hide' : 'Expand'}
              </button>
            </TableCell>
          </TableRow>
          {expandedOrderId === order.id && (
            <TableRow className="border-none hover:bg-transparent">
              <TableCell colSpan={5} className="pt-2 pb-6">
                <UserCard order={order} />
              </TableCell>
            </TableRow>
          )}
        </Fragment>
      ))}
    </Fragment>
  );
}

const INITIAL_HISTORY_FILTERS: OrderHistoryFiltersType = {};

export const OrderHistoryRow = ({
  accordionOpenValue,
}: {
  accordionOpenValue?: string;
}) => {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<OrderHistoryFiltersType>(
    INITIAL_HISTORY_FILTERS,
  );
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
  const [expandedUsers, setExpandedUsers] = useState<Record<number, boolean>>(
    {},
  );
  const [filtersResetKey, setFiltersResetKey] = useState(0);
  const { hasRole } = useHasRole();
  const doctor = hasRole(Role.DOCTOR);

  const handleAccordionOpen = useCallback(() => {
    setFilters(INITIAL_HISTORY_FILTERS);
    setPage(1);
    setFiltersResetKey((k) => k + 1);
  }, []);

  const { data: response, isLoading } = useQuery(
    getOrderHistoryListQuery(page, filters),
  );

  const orders = response?.data || [];
  const counts = response?.counts;
  const meta = response?.meta;

  const handleExpandToggle = (orderId: number) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  const toggleUserOrders = (userId: number) => {
    setExpandedUsers((prev) => ({ ...prev, [userId]: !prev[userId] }));
  };

  return (
    <DashboardTableAccordion
      value="history"
      title="Order history"
      accordionOpenValue={accordionOpenValue}
      onOpen={handleAccordionOpen}
      data={orders}
      isLoading={isLoading}
      emptyMessage="No orders found in history."
      loadingMessage="Loading history..."
      paginationMeta={meta}
      onPageChange={setPage}
      multiRow={true}
      summarySection={
        !doctor && (
          <div className="flex justify-between text-base">
            <span className="text-table-foreground">
              <span className="font-light">Active {counts?.active}</span>
            </span>
            <span className="text-table-foreground">
              <span className="font-light">Inactive {counts?.inactive}</span>
            </span>
            <span className="text-table-foreground">
              <span className="font-light">Churned {counts?.churned}</span>
            </span>
            <span className="text-table-foreground">
              <span className="font-light">Total {counts?.total}</span>
            </span>
          </div>
        )
      }
      filtersComponent={
        <OrderHistoryFilters
          key={filtersResetKey}
          filters={filters}
          onFiltersChange={(newFilters) => {
            setFilters(newFilters);
            setPage(1);
          }}
        />
      }
      renderHeader={() => (
        <>
          <TableHead>Customer</TableHead>
          <TableHead>Last order date</TableHead>
          <TableHead>Subscription period</TableHead>
          <TableHead>Subscription status</TableHead>
          <TableHead className="w-24"></TableHead>
        </>
      )}
      renderRow={(order: OrderHistoryResponse) => (
        <>
          <TableRow className="border-t border-slate-200">
            <TableCell>
              {order.name} {order.surname}
            </TableCell>
            <TableCell>{formatDate(order.last_order_date)}</TableCell>
            <TableCell>
              {formatDate(order.last_order_date)} -{' '}
              {formatDate(order.subscription_end)}
            </TableCell>
            <TableCell>{order.subscription_status}</TableCell>
            <TableCell className="text-right">
              <button
                className="text-primary font-medium hover:underline"
                onClick={() => toggleUserOrders(order.id)}
              >
                View
              </button>
            </TableCell>
          </TableRow>
          {expandedUsers[order.id] && (
            <UserOrdersRows
              userId={order.id}
              expandedOrderId={expandedOrderId}
              onExpandToggle={handleExpandToggle}
            />
          )}
        </>
      )}
    />
  );
};
